import styled from '@emotion/styled';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FlowContainerWrapper from './components/flowContainerWrapper/FlowContainerWrapper';
import Footer from './components/footer';
import Header from './components/header/Header';
import FlowContainer from './flowContainer/Machine-Container';
import KoalafiInitialLoader from './components/koalafi-initial-loader';
import CustomErrorBoundary from './components/customErrorBoundary/custom-error-boundary';
import CustomSecurityErrorBoundary, { SecurityErrorType } from './components/customSecurityErrorBoundary/custom-security-error-boundary';
import ecommMachine from './state-machine/ecomm-machine';
import { useMachine } from './state-machine/machine';
import { acceptedDomain, currentDomain } from './helpers/window/window.helpers';
import { canAccessApplicationDetails, canAccessLeaseAgreement } from './helpers/contractSummaryAccess/contractSummaryAccess';
import { identify as segmentIdentify } from './state-machine/actions/segment';
import { ChannelTypes, FormTypes } from './gql/graphql';
import { AppChannel } from './constants';
import NotAllowedToCreateNewApplications from './pages/notAllowedToCreateNewApplications/NotAllowedToCreateNewApplications';
import ExpiredLink from './pages/expiredLink/ExpiredLink';
import { modalLoaderHelper } from './helpers/modal/modal.helper';
import { inIframe } from './helpers';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { FLAG_SPANISH_ENABLED } from './constants/feature-flags';

const ContentStyling = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 1.5rem 2rem 2.25rem; /* The last number (bottom margin) needs to match height of the footer  */
  flex: 1;
`;

function App() {
  const { t } = useTranslation();
  const memoizedMachine = useMemo(() => ecommMachine(), []);

  const machine = useMachine(memoizedMachine);
  const [state, transition] = machine;

  const { dealer, orderId, application, highestSupportedApplicationMilestone, role, validatedByTokenType, ldClient, networkError } = state.context;

  // Hide loading spinner if showing an error
  const hasDomainError = !!(dealer?.domains && !acceptedDomain(dealer.domains));
  const errorIsBeingRendered = !!networkError || (!state.matches('EntryPointV2') && hasDomainError);
  useEffect(() => {
    if (errorIsBeingRendered) {
      const sendDomain = dealer?.domains?.length ? dealer.domains : [currentDomain()];
      modalLoaderHelper({ stopSpinner: true }, sendDomain);
    }
  }, [errorIsBeingRendered, dealer]);

  const track = useCallback(
    (eventName: string, payload?: object) => {
      window.analytics?.track(eventName, {
        formType: dealer?.formType,
        orderId,
        dealerId: dealer?.id,
        orderFinancingType: dealer?.financingType,
        appChannel: dealer?.isVirtualCard
          ? AppChannel.virtualCard
          : dealer?.channelType === ChannelTypes.BrickAndMortar
          ? AppChannel.brickAndMortar
          : AppChannel.ecomm,
        applicationId: application?.lease?.id || application?.loan?.id,
        applicationNumber: application?.lease?.displayId,
        appLanguage: i18n.resolvedLanguage ?? 'en',
        browserLanguage: navigator.language || navigator.languages?.[0] || null,
        sourceWindowPath: window.location.pathname,
        UI_redesign: 'UI_redesign',
        smRefactor: 'smRefactor',
        ...payload,
      });
    },
    [dealer, application, orderId],
  );
  const page = useCallback(
    (pageName: string, payload?: object) => {
      window.analytics?.page(pageName, {
        formType: dealer?.formType || (dealer?.channelType === ChannelTypes.BrickAndMortar ? FormTypes.Long : FormTypes.Short),
        appLanguage: i18n.resolvedLanguage ?? 'en',
        browserLanguage: navigator.language || navigator.languages?.[0] || null,
        UI_redesign: 'UI_redesign',
        ...payload,
      });
    },
    [dealer],
  );
  const identify = segmentIdentify;

  const [headerGoBackFunc, setHeaderGoBackFunc] = useState<undefined | (() => void)>(undefined);
  const [currentNode, setCurrentNode] = useState<[string] | undefined>(undefined);

  if (networkError) {
    if (networkError.forbiddenError) {
      return (
        <CustomSecurityErrorBoundary
          orderId={orderId ?? ''}
          role={role}
          dealer={dealer}
          error={`Error: ${networkError.forbiddenError.displayText}`}
          errorType={SecurityErrorType.Forbidden403}
          track={track}
          page={page}
          identify={identify}
        />
      );
    }
    if (networkError.unauthorizedError) {
      return (
        <CustomSecurityErrorBoundary
          orderId={orderId ?? ''}
          role={role}
          dealer={dealer}
          error={`Error: ${networkError.unauthorizedError.displayText}`}
          errorType={SecurityErrorType.Unauthorized401}
          track={track}
          page={page}
          identify={identify}
        />
      );
    }
    if (networkError.errorGeneral) {
      const { displayText, developerText, stackTrace } = networkError.errorGeneral;
      const reportedError = [
        'Error: ' + displayText,
        developerText,
        stackTrace instanceof Error ? stackTrace.message : stackTrace ? String(stackTrace) : undefined,
      ]
        .filter(Boolean)
        .join(' | ');
      return (
        <CustomErrorBoundary
          orderId={orderId ?? ''}
          dealer={dealer}
          role={role}
          data-testid="error-boundary"
          error={reportedError}
          body={t(($) => $.error.unableToCompleteRequest)}
          additionalBody={t(($) => $.componentCustomSecurityErrorBoundary.troubleCookies)}
          buttonText={t(($) => $.buttonLabels.exit)}
          track={track}
          page={page}
          identify={identify}
        />
      );
    }
  }

  if (state.matches('EntryPointV2.ExpiredLink')) {
    return <ExpiredLink orderId={orderId ?? ''} dealer={dealer} track={track} page={page} identify={identify} />;
  }

  if (state.matches('EntryPointV2')) {
    if (!inIframe()) {
      return <KoalafiInitialLoader />;
    }
    return <div data-testid="initial-loader" />;
  }

  if (dealer?.domains && !acceptedDomain(dealer.domains)) {
    return (
      <CustomErrorBoundary
        orderId={orderId ?? ''}
        dealer={dealer}
        role={role}
        data-testid="error-boundary"
        error={`Not an acceptable domain: "${currentDomain()}"`}
        body={t(($) => $.error.unableToCompleteRequest)}
        additionalBody={t(($) => $.componentCustomSecurityErrorBoundary.troubleCookies)}
        buttonText={t(($) => $.buttonLabels.exit)}
        track={track}
        page={page}
        identify={identify}
      />
    );
  }

  if (
    !dealer.isAllowedToCreateNewApplications &&
    !canAccessLeaseAgreement(role, highestSupportedApplicationMilestone, application?.lease?.status, validatedByTokenType) &&
    !canAccessApplicationDetails(role)
  ) {
    return (
      <NotAllowedToCreateNewApplications
        orderId={orderId}
        dealer={dealer}
        highestSupportedApplicationMilestone={highestSupportedApplicationMilestone}
        track={track}
        page={page}
        identify={identify}
      />
    );
  }

  return (
    <div
      id="app-content-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        flex: '1',
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.20)',
      }}
    >
      <Header
        orderId={orderId}
        dealer={dealer}
        goBackFunc={headerGoBackFunc}
        currentNode={currentNode}
        track={track}
        page={page}
        identify={identify}
        highestSupportedApplicationMilestone={highestSupportedApplicationMilestone}
        appStatus={application?.lease?.status}
      />
      <ContentStyling>
        <FlowContainerWrapper>
          <FlowContainer send={transition} setHeaderGoBackFunc={setHeaderGoBackFunc} setCurrentNode={setCurrentNode} machine={machine} />
        </FlowContainerWrapper>
      </ContentStyling>
      <Footer languageTogglerEnabled={ldClient?.variation?.(FLAG_SPANISH_ENABLED, false) ?? false} track={track} />
    </div>
  );
}

export default App;
